import { motion } from "framer-motion";
import { Gift, MessageCircle } from "lucide-react";
import { useLang } from "@/LangContext";
import LotusMark from "./LotusMark";

const WA_VOUCHER =
  "https://wa.me/34623552949?text=" +
  encodeURIComponent("Hola Royale! Me gustaría comprar un bono regalo de masaje. ¿Me ayudas con el pago y el código?");

const COPY = {
  es: {
    eyebrow: "Bonos Regalo",
    title: "Regala un momento de calma",
    sub: "Un bono Royale es el regalo que se recuerda: masaje thai y terapéutico en nuestro santuario de Sants. Desde €38.",
    steps: [
      "Escríbenos por WhatsApp y elige tu bono",
      "Te paso el enlace de pago (SumUp o Bizum)",
      "Recibes el bono con su código único al instante",
    ],
    cta: "Pedir mi bono por WhatsApp",
    personal: "Te atiendo personalmente para el pago y los códigos",
  },
  en: {
    eyebrow: "Gift Vouchers",
    title: "Gift a moment of calm",
    sub: "A Royale voucher is a gift to remember: Thai and therapeutic massage in our Sants sanctuary. From €38.",
    steps: [
      "Message us on WhatsApp and choose your voucher",
      "I'll send you the payment link (SumUp or Bizum)",
      "You receive the voucher with its unique code instantly",
    ],
    cta: "Get my voucher via WhatsApp",
    personal: "I personally help you with payment and the codes",
  },
};

export default function GiftVoucher() {
  const { lang } = useLang();
  const t = COPY[lang];

  return (
    <section
      id="bono"
      className="relative py-20 lg:py-24 scroll-mt-16"
      data-testid="gift-voucher-section"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-3xl bg-moss border border-gold/25 px-7 py-12 sm:px-12 sm:py-14"
        >
          <span className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(184,157,98,0.18),transparent_55%)]" />
          <LotusMark className="absolute -bottom-8 -left-8 w-44 h-44 text-gold/10 rotate-12" />

          <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <p className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-goldbright flex items-center gap-3 mb-4">
                <Gift className="w-4 h-4 text-gold" />
                {t.eyebrow}
              </p>
              <h2 className="font-serif font-medium text-3xl sm:text-5xl text-cream leading-[1.08]">
                {t.title}
              </h2>
              <p className="mt-4 text-sage font-light leading-relaxed max-w-lg">{t.sub}</p>
              <ul className="mt-6 flex flex-col gap-2.5">
                {t.steps.map((s, i) => (
                  <li key={s} className="flex items-center gap-3 text-sm text-cream/85">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full border border-gold/40 text-gold font-mono text-[10px] shrink-0">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-center lg:items-end gap-4">
              <a
                href={WA_VOUCHER}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gift-voucher-whatsapp-button"
                className="group inline-flex items-center gap-2.5 rounded-full bg-wamist px-8 py-4 text-[15px] font-medium text-forest hover:scale-[1.03] active:scale-[0.98] transition-transform duration-300 shadow-[0_14px_40px_rgba(37,211,102,0.3)]"
              >
                <MessageCircle className="w-5 h-5" />
                {t.cta}
              </a>
              <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-sage/80 text-center lg:text-right">
                {t.personal}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
